import type { SSEChatOutput } from '~/services/chat/sse';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Prompts } from '@ant-design/x';
import { useXChat } from '@ant-design/x-sdk';
import { GetProp, message } from 'antd';

import { type PopupContext, type PopupProviderProps, DATE } from './_types';

import { useAuthContext } from '~/common/contexts/auth';
import { useGetProfileAgent } from '~/services/agent/get-profile';
import { useSSEChat } from '~/services/chat/sse';
import { useGetConversation } from '~/services/chat-session/get';
import { useGetConversationList } from '~/services/chat-session/get-list';

/** Compare dates and return group key based on time ranges */
const getConversationGroupKey = (value?: string, referenceDate: Date = new Date()): string => {
  /** Date of the conversation */
  const date = new Date(value);

  /** Create date-only versions for comparison (local timezone) */
  const refDateOnly = new Date(referenceDate.getFullYear(), referenceDate.getMonth(), referenceDate.getDate());
  const conversationDateOnly = new Date(date.getFullYear(), date.getMonth(), date.getDate());

  /** Calculate time boundaries */
  const yesterday = new Date(refDateOnly);
  yesterday.setDate(yesterday.getDate() - 1);

  const last7Days = new Date(refDateOnly);
  last7Days.setDate(last7Days.getDate() - 7);

  const last30Days = new Date(refDateOnly);
  last30Days.setDate(last30Days.getDate() - 30);

  /** If the date is today */
  if (conversationDateOnly.getTime() === refDateOnly.getTime()) {
    return DATE.TODAY;
  }

  /** If the date is yesterday */
  if (conversationDateOnly.getTime() === yesterday.getTime()) {
    return DATE.YESTERDAY;
  }

  /** If within last 7 days */
  if (date >= last7Days) {
    return DATE.LAST_7_DAYS;
  }

  /** If within last 30 days */
  if (date >= last30Days) {
    return DATE.LAST_30_DAYS;
  }

  /** Older than 30 days - group by month */
  const year = date.getFullYear();
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  return `month:${year}-${month}`;
};

/**
 * Popup Component Hooks
 */
export const useHooks = ({
  onClose,
  hasMenu,
  quickQuestions,
  session_id: initialSessionId,
  onSessionChange,
}: PopupProviderProps) => {
  /** context */
  const { external_user, token } = useAuthContext();

  /** Get Agent Profile */
  const { data: profileData, reload: reloadProfile } = useGetProfileAgent(external_user, !!token);

  /** states */
  const [, contextHolder] = message.useMessage();
  const [content, setContent] = useState('');
  const [curConversationId, setCurConversationId] = useState<string>(initialSessionId);
  /** Local state to track the current session ID for SSE provider - allows resetting provider on new conversation */
  const [sessionChatId, setSessionChatId] = useState<string | undefined>(initialSessionId);
  /** Ref to track if the current session is newly created to skip history fetch */
  const isNewSessionRef = useRef(false);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [processingSteps, setProcessingSteps] = useState<string[]>([]);
  const [followUpQueries, setFollowUpQueries] = useState<string[]>([]);

  /** Get list conversation */
  const { data: conversationListData, reload: reloadConversationList } = useGetConversationList(external_user, !!token);

  /** Get conversation - Skip history fetch for newly created sessions */
  const { data: conversationData } = useGetConversation(external_user, curConversationId);

  /** Handle open sidebar */
  const handleOpenSidebar = useCallback(() => {
    setSidebarOpen(true);
  }, []);

  /** Handle close sidebar */
  const handleCloseSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  useEffect(() => {
    /** If profileData is not available, return early */
    if (!profileData?.expires_in) return;

    /** if expires_in <= 60s then refresh immediately */
    if (profileData.expires_in <= 60) {
      reloadProfile();
      return;
    }

    /** set timeout to refresh before expiration in 60s */
    const timeout = setTimeout(
      () => {
        reloadProfile();
      },
      (profileData.expires_in - 60) * 1000,
    );

    return () => clearTimeout(timeout);
  }, [profileData?.expires_in, reloadProfile]);

  /**
   * Handles SSE update chunks to maintain processing steps state.
   * Only shows the latest step (replaces previous step).
   */
  const handleSSEUpdate = useCallback(
    (chunk: SSEChatOutput) => {
      if (chunk?.metadata?.type === 'step') {
        // Only show the latest step, replacing any previous step
        setProcessingSteps([chunk?.text || '']);
        return;
      }

      if (chunk?.metadata?.type === 'follow_up_queries') {
        const queries = Array.isArray(chunk.follow_up_queries) ? (chunk.follow_up_queries as string[]) : [];
        setFollowUpQueries(queries);
        return;
      }

      if (chunk?.metadata?.type === 'message') {
        setProcessingSteps([]);
      }
    },
    [setProcessingSteps, setFollowUpQueries],
  );

  /**
   * Handles SSE success event to sync session and processing state.
   */
  const handleSSESuccess = useCallback(
    (chunks: SSEChatOutput[]) => {
      chunks.map((chunk) => {
        if (chunk?.metadata?.type === 'chat_session' && chunk?.session_id) {
          isNewSessionRef.current = true;
          setCurConversationId((prev) => prev ?? (chunk.session_id as string));
        }
      });

      setProcessingSteps([]);
      reloadConversationList();
    },
    [reloadConversationList],
  );

  /**
   * Handles SSE error event. Reserved for future logging.
   */
  const handleSSError = useCallback((error?: Error) => {
    void error;
  }, []);

  const sseOptions = useMemo(
    () => ({
      config: {
        baseURL: import.meta.env.VITE_API_URL || 'http://localhost:8000/v1',
        external_user_id: external_user,
        session_id: sessionChatId,
        token,
      },
      callbacks: {
        onUpdate: handleSSEUpdate,
        onSuccess: handleSSESuccess,
        onError: handleSSError,
      },
    }),
    [sessionChatId, external_user, handleSSESuccess, handleSSEUpdate, handleSSError, token],
  );

  /** SSE Chat Provider setup */
  const { provider, conversationKey } = useSSEChat(sseOptions);

  const { onRequest, messages, setMessages, isRequesting, abort } = useXChat({
    provider,
    conversationKey,
  });

  /**
   * Submits a user message.
   *
   * Sends the user's message to the SSE provider and clears the input field.
   */
  const onSubmit = useCallback(
    (content: string) => {
      if (!content) return;
      onRequest({
        message: { content, role: 'user' },
        external_user_id: external_user,
        session_id: curConversationId,
      });
      setContent('');
      setFollowUpQueries([]);
    },
    [curConversationId, external_user, onRequest],
  );

  /**
   * Handles item clicks in the prompts list.
   *
   * Sends the selected prompt's description as a user message.
   */
  const onPromptsItemClick: GetProp<typeof Prompts, 'onItemClick'> = useCallback(
    (info) => {
      onRequest({
        message: {
          content: info.data.description as string,
          role: 'user',
        },
        external_user_id: external_user,
        session_id: curConversationId,
      });
    },
    [curConversationId, external_user, onRequest],
  );

  /**
   * Handles the creation of a new conversation.
   * Back to chat content
   */
  const resetConversationState = useCallback(() => {
    if (isRequesting) {
      abort();
    }

    setProcessingSteps([]);
    setFollowUpQueries([]);
    isNewSessionRef.current = false;
    setSessionChatId(undefined);

    /** Clear the current conversation ID to reset the chat content */
    setCurConversationId(undefined);

    /** Set messages to display the placeholder node */
    setMessages([]);

    onSessionChange?.(undefined);
  }, [abort, isRequesting, setMessages, onSessionChange]);

  const handleAddConversation = useCallback(async () => {
    resetConversationState();
  }, [resetConversationState]);

  /**
   * Handle cancel button click - abort SSE streaming
   */
  const handleCancel = useCallback(async () => {
    if (isRequesting) {
      abort();
    }
    setProcessingSteps([]);
  }, [abort, isRequesting]);
  /**
   * Changes the active conversation.
   *
   * Updates the current conversation and loads its message history.
   */
  const handleActiveChange = useCallback(async (val: string) => {
    isNewSessionRef.current = false;
    setSessionChatId(val);
    setCurConversationId(val);
    setFollowUpQueries([]);
  }, []);

  /**
   * Watch for conversation data changes and update messages
   */
  useEffect(() => {
    /** If no conversation selected, skip */
    if (!curConversationId) {
      setMessages([]);
      return;
    }

    /** Skip history fetch for newly created sessions - messages already in UI from SSE */
    if (isNewSessionRef.current) return;

    if (!conversationData?.history_messages) return;

    // Convert legacy Message format to SSEChatMessage format
    setMessages(
      conversationData.history_messages.map((message) => ({
        id: crypto.randomUUID(),
        message: {
          ...message,
          role: message.type === 'human' ? 'user' : 'assistant',
        },
        status: 'local',
      })),
    );
  }, [conversationData, curConversationId, setMessages]);

  const conversationItems = useMemo(() => {
    if (!conversationListData?.items) {
      return undefined;
    }

    const today = new Date();
    return conversationListData.items.map((conversation) => {
      const lastActivityAt = conversation.updated_at || conversation.created_at;
      return {
        key: conversation.id,
        label: conversation.name,
        lastActivityAt,
        group: getConversationGroupKey(lastActivityAt, today),
      };
    });
  }, [conversationListData]);

  const onReload = useCallback(async (): Promise<void> => {
    await reloadConversationList?.();
  }, [reloadConversationList]);

  /**
   * Resets the message list when the component is mounted.
   */
  useEffect(() => {
    setMessages([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Sync session ID changes with parent component via onSessionChange callback.
   */
  useEffect(() => {
    if (curConversationId) {
      onSessionChange?.(curConversationId);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [curConversationId]);

  /** return */
  return useMemo(
    (): PopupContext => ({
      states: {
        profileData,
        content,
        provider,
        contextHolder,
        conversationItems,
        curConversationId,
        messages,
        sidebarOpen,
        isRequesting,
        processingSteps,
        followUpQueries,
        hasMenu,
        quickQuestions,
      },
      events: {
        onSubmit,
        setContent,
        handleActiveChange,
        handleAddConversation,
        resetConversationState,
        onPromptsItemClick,
        handleOpenSidebar,
        handleCloseSidebar,
        handleCancel,
        onReload,
        onClose,
      },
    }),
    [
      profileData,
      content,
      provider,
      contextHolder,
      conversationItems,
      curConversationId,
      messages,
      sidebarOpen,
      isRequesting,
      processingSteps,
      hasMenu,
      quickQuestions,
      onSubmit,
      handleActiveChange,
      handleAddConversation,
      onPromptsItemClick,
      handleOpenSidebar,
      handleCloseSidebar,
      handleCancel,
      onReload,
      onClose,
      resetConversationState,
      followUpQueries,
    ],
  );
};
